import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid, Tooltip } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import MilitaryBadge from "Components/Generic/Icons/MilitaryBadge";
import useTimezoneDate from "Hooks/useTimezoneDate";
import { StarSlash } from "iconsax-react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { CertificationType } from "Util/enums";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import { MAX_SIZE } from "..";
import { Input, Select } from "../../../../../../../../../../../components/Form";
import IconPdfDocument from "../../../../../../../../../../../components/Generic/Icons/IconPdfDocument";
import IconUpload from "../../../../../../../../../../../components/Generic/Icons/IconUpload";
import IconXCircle from "../../../../../../../../../../../components/Generic/Icons/IconXCircle";
import Modal from "../../../../../../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../../../../../../components/Generic/Toast/NotificationToast";
import useYupValidation from "../../../../../../../../../../../hooks/useYupValidation";
import { MemberCertificationStatus } from "../../../../../../../../../../../models/member";
import { MemberCertification } from "../../../../../../../../../../../models/members/member.certification";
import useStyles, {
  DeleteButton,
  ContentWrapper,
  ModalSection,
  Row,
  UploadDescription,
  UploadedSection,
  UploadFilename,
  UploadFilenameBox,
  UploadSection,
  UploadTitle,
} from "./styles";

export type ManageCertificationHandles = {
  open: (root?: MemberCertification) => void;
  close: () => void;
};

type Props = {
  isLoading?: boolean;
  onAddNewCertification: (data: MemberCertification, file: File | null) => void;
};

const ManageCertificationComponent: ForwardRefRenderFunction<ManageCertificationHandles, Props> = (
  { isLoading = false, onAddNewCertification },
  ref
) => {
  const classes = useStyles();

  const { t: translate } = useTranslation();

  const [isOpened, setIsOpened] = useState(false);
  const [, setRootData] = useState<MemberCertification | null>(null);

  const [description, setDescription] = useState("");
  const [type, setType] = useState<CertificationType>(CertificationType.STANDARD);
  const [url, setUrl] = useState("");
  const [reviewDate, setReviewDate] = useState<string>();
  const [expireDate, setExpireDate] = useState<string>();
  const [file, setFile] = useState<File | null>(null);

  const { getDateValueUtcTimezone, getSaveDateValue } = useTimezoneDate();

  const onDropFile = (files: File[]) => {
    if (!files || !files.length) return;

    const file = files[0];

    const splittedFileName = file.name.split(".");
    const extensionFromName = splittedFileName[splittedFileName.length - 1];

    if (file?.size && file?.size > MAX_SIZE) {
      NotificationToast.error(translate("theFileMustBeLessThan2mbInSize"));

      return;
    }

    if (file && extensionFromName === "pdf") {
      setFile(file);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: onDropFile,
    maxFiles: 1,
    accept: ["application/pdf"],
  });

  const onResetFields = useCallback(() => {
    setDescription("");
    setType(CertificationType.STANDARD);
    setUrl("");
    setReviewDate(undefined);
    setExpireDate(undefined);
    setFile(null);
  }, []);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { getErrorByField } = useYupValidation(errors);

  const schema = yup.object().shape({
    description: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    type: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const onValidate = () => {
    try {
      schema.validateSync(
        {
          description,
          type,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
      }

      return false;
    }
  };

  const onSubmitForm = () => {
    if (onValidate()) {
      const data = {
        description,
        type,
        url,
        randomUuid: uuid(),
        expireDate,
        reviewDate,
        isApproved: false,
        fileId: null,
        file: null,
        status: MemberCertificationStatus.WAITING,
      };

      onAddNewCertification(data, file);
    }
  };

  const renderUploadFileSection = () => {
    if (file) {
      return (
        <UploadedSection>
          <div>
            <IconPdfDocument color={colors.error500} height={16} width={16} />
            <Tooltip title={file?.name}>
              <UploadFilenameBox>
                <UploadFilename>{file?.name}</UploadFilename>
              </UploadFilenameBox>
            </Tooltip>
          </div>
          <DeleteButton onClick={() => setFile(null)}>
            <IconXCircle width={20} height={20} color={colors.error500} />
          </DeleteButton>
        </UploadedSection>
      );
    }

    return (
      <UploadSection {...getRootProps()}>
        <input {...getInputProps()} />
        <IconUpload color={colors.brandBlue500} height={24} width={24} />
        <UploadTitle>{translate("dragOrClickToUploadYourFile")}</UploadTitle>
        <UploadDescription>{`${translate("maxSize")} ${translate("2Mb")}`}</UploadDescription>
        <UploadDescription>{`${translate("formats")}: ${translate(
          "pdf"
        ).toUpperCase()}`}</UploadDescription>
      </UploadSection>
    );
  };

  const onOpenModal = useCallback((root?: MemberCertification) => {
    setRootData(root || null);
    setIsOpened(true);
  }, []);

  const onCloseModal = useCallback(() => {
    onResetFields();
    setRootData(null);
    setIsOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <Modal
      open={isOpened}
      handleClose={onCloseModal}
      title={translate("newCertification")}
      data-cy="fleetModal"
      dataCyTitle="fleetModalTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: onCloseModal,
        dataCySave: "submitChangesButton",
        textSave: translate("create"),
        loading: isLoading,
        onSave: onSubmitForm,
        classesCancel: classes.cancelText,
      }}
      showStatusField={false}
      className={classes.modal}
      modalFooterCustomClass={classes.modalFooter}
    >
      <ModalSection>
        <Grid container direction="row" alignItems="flex-start" spacing={3}>
          <Row item xs={6}>
            <Input
              name="description"
              label={translate("description")}
              required
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              error={!!getErrorByField("description")}
              errorMessage={getErrorByField("description")}
            />
          </Row>
          <Row item xs={6}>
            <Select
              label={translate("type")}
              required
              value={type}
              onChange={(event: SelectChangeEvent<any>) => setType(event.target.value)}
              error={!!getErrorByField("type")}
              errorMessage={getErrorByField("type")}
              inputProps={{ className: classes.input }}
            >
              <MenuItem value={CertificationType.STANDARD}>{CertificationType.STANDARD}</MenuItem>
              <MenuItem value={CertificationType.VIP}>
                <ContentWrapper>
                  {CertificationType.VIP}
                  <StarSlash
                    size={24}
                    color={colors.warning500}
                    fill={colors.white}
                    variant="Bold"
                  />
                </ContentWrapper>
              </MenuItem>
              <MenuItem value={CertificationType.MILITARY}>
                <ContentWrapper>
                  {CertificationType.MILITARY} <MilitaryBadge height="24" width="24" />
                </ContentWrapper>
              </MenuItem>
            </Select>
          </Row>
          <Row item xs={12}>
            <Input
              name="url"
              label={translate("url").toUpperCase()}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
          </Row>
          <Row item xs={6}>
            <Input
              name="reviewDate"
              label={translate("review")}
              type="date"
              value={reviewDate ? getDateValueUtcTimezone(reviewDate) : ""}
              onChange={(event) => {
                setReviewDate(getSaveDateValue(event.target.value));
              }}
            />
          </Row>
          <Row item xs={6}>
            <Input
              name="expireDate"
              label={translate("expire")}
              type="date"
              value={expireDate ? getDateValueUtcTimezone(expireDate) : ""}
              onChange={(event) => {
                setExpireDate(getSaveDateValue(event.target.value));
              }}
            />
          </Row>
          <Grid item xs={12}>
            {renderUploadFileSection()}
          </Grid>
        </Grid>
      </ModalSection>
    </Modal>
  );
};

const ManageCertification = forwardRef(ManageCertificationComponent);

export default ManageCertification;
